/**
 * Gomoku AI Engine
 * Adjustable difficulty: Easy, Medium, Hard (Heuristic Threat Evaluation)
 */
import { BOARD_SIZE, EMPTY, PLAYER_BLACK, PLAYER_WHITE } from './engine.js';

export class GomokuAI {
  constructor(difficulty = 'medium') {
    this.difficulty = difficulty;
  }

  setDifficulty(diff) {
    this.difficulty = diff;
  }

  findBestMove(engine, aiPlayer = PLAYER_WHITE) {
    const opponent = aiPlayer === PLAYER_WHITE ? PLAYER_BLACK : PLAYER_WHITE;
    const board = engine.board;

    // First move on empty board: center or near center
    if (engine.moveHistory.length === 0) {
      return { x: 7, y: 7 };
    }

    // Easy mode: pick random move from candidate moves near existing stones
    if (this.difficulty === 'easy') {
      const candidates = this.getCandidateMoves(board, 1);
      // 30% chance to blunder a random candidate
      if (Math.random() < 0.3 && candidates.length > 0) {
        return candidates[Math.floor(Math.random() * candidates.length)];
      }
    }

    // 1. Immediate Win Check for AI
    const candidates = this.getCandidateMoves(board, 2);
    for (const move of candidates) {
      board[move.y][move.x] = aiPlayer;
      const win = engine.checkWin(move.x, move.y, aiPlayer).win;
      board[move.y][move.x] = EMPTY;
      if (win) return move;
    }

    // 2. Immediate Block Opponent Win Check
    for (const move of candidates) {
      board[move.y][move.x] = opponent;
      const win = engine.checkWin(move.x, move.y, opponent).win;
      board[move.y][move.x] = EMPTY;
      if (win) return move;
    }

    if (this.difficulty === 'medium') {
      // Evaluate best move using 1-ply threat scoring
      let bestScore = -Infinity;
      let bestMove = candidates[0] || { x: 7, y: 7 };

      for (const move of candidates) {
        const attackScore = this.evaluatePoint(board, move.x, move.y, aiPlayer);
        const defenseScore = this.evaluatePoint(board, move.x, move.y, opponent);
        const totalScore = attackScore * 1.1 + defenseScore;

        if (totalScore > bestScore) {
          bestScore = totalScore;
          bestMove = move;
        }
      }
      return bestMove;
    }

    // Hard Mode: 2-ply Minimax with Alpha-Beta Pruning
    let bestScore = -Infinity;
    let bestMove = candidates[0] || { x: 7, y: 7 };

    // Sort candidates by combined static threat score
    const scoredCandidates = candidates.map((m) => {
      const score = this.evaluatePoint(board, m.x, m.y, aiPlayer) * 1.2 + this.evaluatePoint(board, m.x, m.y, opponent);
      return { ...m, score };
    }).sort((a, b) => b.score - a.score).slice(0, 12); // Beam search top 12

    let alpha = -Infinity;
    const beta = Infinity;

    for (const move of scoredCandidates) {
      board[move.y][move.x] = aiPlayer;
      const score = this.minimax(engine, board, 2, false, alpha, beta, aiPlayer, opponent);
      board[move.y][move.x] = EMPTY;

      if (score > bestScore) {
        bestScore = score;
        bestMove = { x: move.x, y: move.y };
      }
      alpha = Math.max(alpha, bestScore);
    }

    return bestMove;
  }

  minimax(engine, board, depth, isMaximizing, alpha, beta, aiPlayer, humanPlayer) {
    if (depth === 0) {
      return this.evaluateBoard(board, aiPlayer, humanPlayer);
    }

    const candidates = this.getCandidateMoves(board, 1).slice(0, 8);
    if (candidates.length === 0) return 0;

    if (isMaximizing) {
      let maxEval = -Infinity;
      for (const m of candidates) {
        board[m.y][m.x] = aiPlayer;
        if (engine.checkWin(m.x, m.y, aiPlayer).win) {
          board[m.y][m.x] = EMPTY;
          return 50000;
        }
        const evalScore = this.minimax(engine, board, depth - 1, false, alpha, beta, aiPlayer, humanPlayer);
        board[m.y][m.x] = EMPTY;
        maxEval = Math.max(maxEval, evalScore);
        alpha = Math.max(alpha, evalScore);
        if (beta <= alpha) break;
      }
      return maxEval;
    } else {
      let minEval = Infinity;
      for (const m of candidates) {
        board[m.y][m.x] = humanPlayer;
        if (engine.checkWin(m.x, m.y, humanPlayer).win) {
          board[m.y][m.x] = EMPTY;
          return -50000;
        }
        const evalScore = this.minimax(engine, board, depth - 1, true, alpha, beta, aiPlayer, humanPlayer);
        board[m.y][m.x] = EMPTY;
        minEval = Math.min(minEval, evalScore);
        beta = Math.min(beta, evalScore);
        if (beta <= alpha) break;
      }
      return minEval;
    }
  }

  getCandidateMoves(board, distance = 1) {
    const moves = [];
    const visited = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(false));

    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        if (board[y][x] !== EMPTY) {
          for (let dy = -distance; dy <= distance; dy++) {
            for (let dx = -distance; dx <= distance; dx++) {
              const nx = x + dx;
              const ny = y + dy;
              if (nx >= 0 && nx < BOARD_SIZE && ny >= 0 && ny < BOARD_SIZE) {
                if (board[ny][nx] === EMPTY && !visited[ny][nx]) {
                  visited[ny][nx] = true;
                  moves.push({ x: nx, y: ny });
                }
              }
            }
          }
        }
      }
    }
    return moves;
  }

  evaluatePoint(board, x, y, player) {
    let totalScore = 0;
    const directions = [
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 1, dy: 1 },
      { dx: 1, dy: -1 }
    ];

    for (const { dx, dy } of directions) {
      let count = 1;
      let openEnds = 0;

      // Positive ray
      let step = 1;
      while (step <= 4) {
        const nx = x + dx * step;
        const ny = y + dy * step;
        if (nx < 0 || nx >= BOARD_SIZE || ny < 0 || ny >= BOARD_SIZE) break;
        if (board[ny][nx] === player) {
          count++;
          step++;
        } else if (board[ny][nx] === EMPTY) {
          openEnds++;
          break;
        } else {
          break; // Opponent piece blocks
        }
      }

      // Negative ray
      step = 1;
      while (step <= 4) {
        const nx = x - dx * step;
        const ny = y - dy * step;
        if (nx < 0 || nx >= BOARD_SIZE || ny < 0 || ny >= BOARD_SIZE) break;
        if (board[ny][nx] === player) {
          count++;
          step++;
        } else if (board[ny][nx] === EMPTY) {
          openEnds++;
          break;
        } else {
          break;
        }
      }

      totalScore += this.getPatternScore(count, openEnds);
    }
    return totalScore;
  }

  getPatternScore(count, openEnds) {
    if (count >= 5) return 100000;
    if (count === 4) {
      if (openEnds === 2) return 10000;
      if (openEnds === 1) return 1200;
    }
    if (count === 3) {
      if (openEnds === 2) return 1000;
      if (openEnds === 1) return 150;
    }
    if (count === 2) {
      if (openEnds === 2) return 100;
      if (openEnds === 1) return 15;
    }
    return 1;
  }

  evaluateBoard(board, aiPlayer, humanPlayer) {
    let score = 0;
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        if (board[y][x] === aiPlayer) {
          score += this.evaluatePoint(board, x, y, aiPlayer);
        } else if (board[y][x] === humanPlayer) {
          score -= this.evaluatePoint(board, x, y, humanPlayer) * 1.1;
        }
      }
    }
    return score;
  }
}
